import { IAddress } from '@libs/api'
import { Container } from '@libs/components'
import { Spin } from 'antd'
import { getIn, useFormikContext } from 'formik'
import debounce from 'lodash/debounce'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { INewOrderState } from '../context'
import { useOrderFormAddress } from './hooks'
import Styled from './Styled'

export interface IOrderFormAddress {
	name: string
	onChange: (option: any) => void
	idCheck?: boolean
	mergeUpdate: (newAddress: IAddress) => RecursivePartialType<INewOrderState>
}

/*
An AutoComplete rather than a Select: a Select empties its text box whenever its list closes, so going back to correct a
house or flat number meant typing the whole address again. Here the text box keeps what it holds, and the form field
holds only an address picked from the list. Editing the text empties the field until an address is picked again, so the
form cannot be sent with an address nobody chose.

An option whose data carries `__internal_refine` is a step on the way to an address (a street or a house number from the
Danish address service). Picking one searches its text at once and keeps the list open on the next level.
*/
const OrderFormAddress: FC<IOrderFormAddress> = ({ name, onChange, idCheck = false, mergeUpdate }) => {
	const { setAddressPrediction, setAddressQuery, addressAutocomplete, providerSupportsSubpremise, t } =
		useOrderFormAddress({ name, idCheck, mergeUpdate })
	const { values, setFieldValue, setFieldTouched } = useFormikContext()
	const chosenAddress = getIn(values, name)
	const [addressText, setAddressText] = useState<string>(typeof chosenAddress === 'string' ? chosenAddress : '')
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	// The list closes once after every pick; a narrowing pick refuses that one close so the next level shows at once.
	const shouldRefuseNextCloseRef = useRef(false)

	const debouncedSetAddressQuery = useMemo(() => debounce(setAddressQuery, 250), [setAddressQuery])

	// The field also changes from outside, e.g. the manual edit modal, and the text box follows it
	useEffect(() => {
		if (typeof chosenAddress === 'string' && chosenAddress) {
			setAddressText(chosenAddress)
		}
	}, [chosenAddress])

	const searchAddress = (text: string) => {
		setAddressText(text)
		debouncedSetAddressQuery(text)
	}

	return (
		<Styled.AddressAutoComplete
			value={addressText}
			options={addressAutocomplete.options}
			open={isDropdownOpen}
			onDropdownVisibleChange={open => {
				if (!open && shouldRefuseNextCloseRef.current) {
					shouldRefuseNextCloseRef.current = false
					return
				}

				setIsDropdownOpen(open)
			}}
			notFoundContent={
				addressAutocomplete.isStillLoading ? (
					<Container display={'flex'} justifyContent={'center'}>
						<Spin />
					</Container>
				) : undefined
			}
			placeholder={
				providerSupportsSubpremise
					? t('common.placeholders.minChars', { min: 3 })
					: t('common.placeholders.specificAddressAutocomplete')
			}
			onSearch={text => {
				searchAddress(text)
				if (chosenAddress) {
					setFieldValue(name, '')
				}
			}}
			onSelect={(value: string, option: any) => {
				if (option?.data?.__internal_refine) {
					shouldRefuseNextCloseRef.current = true
					searchAddress(option.data.text ?? value)
					return
				}

				setAddressText(value)
				setFieldValue(name, value)
				setAddressPrediction(option.data)
				onChange(option)
			}}
			onBlur={() => setFieldTouched(name, true)}
		/>
	)
}

export default OrderFormAddress
